/**
 * spiderSounds — Web-Audio synthesized SFX for the Spider-Man theme.
 * We SYNTHESIZE the sounds instead of shipping .mp3 assets because:
 *   • ~0 KB payload (files would add ~50-200 KB just for one-shots)
 *   • Instant playback — no fetch delay on the first thwip
 *   • No CORS/CDN concerns
 *
 * All API calls are safe to run without a user gesture — Web Audio
 * will silently refuse if the browser has locked audio, but we don't
 * throw. Callers can hit `unlock()` on the first user tap to prime
 * the context (some iOS versions require this).
 */

let ctx = null;

function getCtx() {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  } catch { ctx = null; }
  return ctx;
}

/** Prime the audio context — call once on the first user gesture
 *  (tap/click) so iOS Safari allows subsequent programmatic sounds. */
export function unlock() {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") { c.resume().catch(() => {}); }
}

/** Master mute — read from localStorage so the user's preference
 *  survives navigation. Defaults to ON when the Spider-Man theme is
 *  active (see App.js wiring). */
function isMuted() {
  try { return localStorage.getItem("spider:muted") === "1"; }
  catch { return false; }
}
export function setMuted(m) {
  try { localStorage.setItem("spider:muted", m ? "1" : "0"); }
  catch { /* ignore */ }
}

/** THWIP — the classic web-shooter chirp. A short high-pitched sweep
 *  from ~2400Hz down to ~800Hz with a quick pluck envelope. */
export function playThwip() {
  if (isMuted()) return;
  const c = getCtx(); if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(2400, t);
  osc.frequency.exponentialRampToValueAtTime(800, t + 0.08);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.22, t + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.16);
}

/** WHOOSH — swing sound. Filtered noise with a low-to-high pitch
 *  bend so it reads as air rushing past. */
export function playWhoosh() {
  if (isMuted()) return;
  const c = getCtx(); if (!c) return;
  const t = c.currentTime;
  // Short white noise buffer
  const dur = 0.55;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.setValueAtTime(400, t);
  filter.frequency.exponentialRampToValueAtTime(1800, t + dur * 0.7);
  filter.Q.value = 3;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.25, t + 0.08);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(filter).connect(gain).connect(c.destination);
  src.start(t);
  src.stop(t + dur);
}

/** POW — a punchy comic-book "hit" for the milestone reveal.
 *  Low-mid oscillator drop with a quick pluck. */
export function playPow() {
  if (isMuted()) return;
  const c = getCtx(); if (!c) return;
  const t = c.currentTime;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(220, t);
  osc.frequency.exponentialRampToValueAtTime(80, t + 0.25);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(0.3, t + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
  osc.connect(gain).connect(c.destination);
  osc.start(t);
  osc.stop(t + 0.32);
}
